import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Truck, PackagePlus, PackageSearch, ScanBarcode, AlertTriangle } from "lucide-react";
import { useVehicleCategories } from "@/lib/vehicleLists";
import { useDonorStageLabel } from "@/lib/vehicleLists";
import { CreatePartFromTruck } from "@/components/CreatePartFromTruck";
import { MatchBadge, MatchDetail } from "@/components/MatchBadge";
import { CaptureGapNote } from "@/components/search/OnDonorBand";
import { SyntheticDataBanner } from "@/components/SyntheticDataBanner";
import { ScanField } from "@/components/ScanField";
import { useSearchEngine, reportSearchOpened } from "@/components/search/engine";
import { TruckFilterRail } from "@/components/search/TruckFilterRail";
import { useOwnTagOpen } from "@/components/search/SearchBox";

function TruckCard({ d, onCreatePart, onOpen }) {
  const stageLabel = useDonorStageLabel();
  return (
    <div data-testid={`truck-hit-${d.id}`} className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-3">
      <Truck className="w-5 h-5 text-[#B01E28] shrink-0" />
      <Link to={`/donors/${d.id}`} data-testid={`truck-result-${d.stock_number}`} className="min-w-0" onClick={() => onOpen && onOpen(d)}>
        <div className="text-sm font-bold text-slate-800 hover:text-[#B01E28]">{d.stock_number} · {[d.year, d.make, d.model].filter(Boolean).join(" ") || "—"}</div>
        <div className="text-[11px] text-slate-400">{d.vin || "no VIN"}</div>
      </Link>
      <div className="flex items-center gap-1.5 flex-wrap">
        <MatchBadge match={d.match} />
        {(d.specs_preview || []).slice(0, 4).map((s) => <Badge key={s.key} className="bg-slate-100 text-slate-600 text-[10px] font-normal">{s.label}: {String(s.value)}{s.unit ? ` ${s.unit}` : ""}</Badge>)}
      </div>
      <span className="flex-1" />
      <Badge className="bg-slate-100 text-slate-600 text-[10px]">{stageLabel(d.status)}</Badge>
      <Link to={`/donors/${d.id}?parts=1`} data-testid={`truck-parts-link-${d.stock_number}`}
        className="inline-flex items-center gap-1 rounded-lg h-8 px-2.5 border border-slate-200 text-slate-600 hover:border-[#B01E28] hover:text-[#B01E28] text-xs">
        <PackageSearch className="w-3.5 h-3.5" />Parts from this truck
      </Link>
      {onCreatePart && (
        <Button data-testid={`truck-create-part-${d.stock_number}`} size="sm" variant="outline" onClick={() => onCreatePart(d)} className="rounded-lg h-8 border-[#B01E28] text-[#B01E28] hover:bg-red-50 text-xs">
          <PackagePlus className="w-3.5 h-3.5 mr-1" />Create part
        </Button>
      )}
      {d.match && <div className="w-full"><MatchDetail match={d.match} /></div>}
    </div>
  );
}

// Reusable truck finder: serial/VIN floor-lookup (mode 1) + shared-engine spec/keyword search
// (mode 2) + create-part. BOTH modes are server-side + index-backed. Mode 2 runs the SAME
// useSearchEngine({scope:"trucks"}) + TruckFilterRail as the Vehicles list — one engine, one
// filter UI, no parallel implementations. urlBacked:false so it can live inside the Find-a-Part
// page without hijacking that page's parts-search URL.
export function TruckFinder({ canCreatePart = true }) {
  const engine = useSearchEngine({ scope: "trucks", urlBacked: false, pageSize: 50, box: "search_trucks" });
  const categories = useVehicleCategories();
  const [lookupQ, setLookupQ] = useState("");
  const [lookupRes, setLookupRes] = useState(null);
  const [lookupEcho, setLookupEcho] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [cpt, setCpt] = useState(null);

  const catName = (code) => (categories.find((c) => c.code === code)?.name) || code;
  const openCreate = canCreatePart ? (d) => setCpt({ donorId: d.id, donorStock: d.stock_number }) : null;

  const doLookup = async (val) => {
    const term = ((val ?? lookupQ) || "").trim();
    if (term.length < 3) {
      scanRefuse("AT LEAST 3 CHARACTERS OF A SERIAL OR VIN");
      toast.error("Type or scan at least 3 characters of a serial or VIN."); return;
    }
    setLookingUp(true);
    try {
      const { data } = await api.get("/truck-lookup", { params: { q: term, box: "serial_lookup" } });
      const n = data.total ?? (data.items || []).length;
      if (n) scanAccept(`${n} VEHICLE${n === 1 ? "" : "S"}`);
      else scanRefuse("NO VEHICLE CARRIES THAT NUMBER");
      setLookupRes(data); setLookupEcho(term);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      scanRefuse(msg || "LOOKUP FAILED"); toast.error(msg);
    }
    finally { setLookingUp(false); }
  };

  // ENTER AND SCANNING (S4, his rule): a truck's stock # or one of our part tags opens it; a serial, a VIN or any
  // other barcode gets the list, even with one match.
  // (no "typed on since Enter" check here: the scan field empties itself on submit, so there is nothing to compare)
  const openOwnTag = useOwnTagOpen("Find a Part", "truck-lookup");
  const onLookupEnter = async (v) => {
    const t = (v || "").trim();
    if (await openOwnTag(t)) return;
    doLookup(t);
  };

  const res = engine.res;
  const q = engine.sp.get("q") || "";
  const cat = engine.sp.get("category") || "";
  const nFilters = (() => { try { return JSON.parse(engine.sp.get("truck_filters") || "[]").filter((x) => x && x.value !== "" && x.value != null).length; } catch { return 0; } })();

  return (
    <div data-testid="truck-finder" className="space-y-5">
      <SyntheticDataBanner testid="tf-synthetic-banner" />
      {/* MODE 1 — exact serial / VIN lookup (highest-frequency floor question: "what's this off of?") */}
      <div data-testid="tf-mode-lookup" className="rounded-2xl border-2 border-[#B01E28]/20 bg-gradient-to-br from-red-50/40 to-orange-50/30 p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#B01E28] text-white text-[11px] font-bold shrink-0">1</span>
          <ScanBarcode className="w-5 h-5 text-[#B01E28]" />Find a vehicle by serial or VIN
        </div>
        <p className="text-xs text-slate-500 -mt-1.5">Have a part in hand? Scan its barcode/QR with the camera or a scan gun, or type the number stamped on it (engine, trans, diff, dump-box serial) or the VIN. This finds the <b>exact vehicle</b> that number is recorded on.</p>
        <ScanField testid="truck-lookup" label="" submitLabel="Look up" busy={lookingUp} uppercase autoFocus={false}
          placeholder="Scan or type a serial or VIN"
          onValueChange={(v) => setLookupQ(v)} onScan={onLookupEnter} />
        {lookupRes && (
          <div className="space-y-2" data-testid="truck-lookup-results">
            <div data-testid="truck-lookup-echo" className="text-xs text-slate-600">
              Serial / VIN lookup for <span className="text-[#B01E28] font-semibold font-mono">“{lookupEcho}”</span> — <b className="text-slate-900">{lookupRes.total ?? lookupRes.items.length}</b> vehicle{(lookupRes.total ?? lookupRes.items.length) === 1 ? "" : "s"}
            </div>
            {lookupRes.capped && (
              <div data-testid="truck-lookup-capped" className="flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Showing the first <b>{lookupRes.items.length}</b> of <b>{lookupRes.total}</b>. The rest are not hidden from you — type more of the number to narrow it down. The first characters of a VIN are shared by every truck of the same build.</span>
              </div>
            )}
            {lookupRes.duplicate && <div className="flex items-center gap-1.5 text-xs text-red-600 font-semibold"><AlertTriangle className="w-4 h-4" />That number is on {lookupRes.total ?? lookupRes.items.length} trucks — possible duplicate entry.</div>}
            {lookupRes.items.length === 0 ? <div className="text-sm text-slate-400">No vehicle carries that serial or VIN.</div>
              : lookupRes.items.map((d, i) => <TruckCard key={d.id} d={d} onCreatePart={openCreate} onOpen={() => reportSearchOpened(lookupRes.search_record_id, i + 1)} />)}
          </div>
        )}
      </div>

      {/* MODE 2 — browse by keyword and/or spec filters (SHARED engine + filter rail) */}
      <div data-testid="tf-mode-search" className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#E0781A] text-white text-[11px] font-bold shrink-0">2</span>
          <PackageSearch className="w-5 h-5 text-[#E0781A]" />Browse vehicles by keyword or spec
        </div>
        <p className="text-xs text-slate-500 -mt-1.5">Don’t have a number? Type a <b>keyword</b> (stock #, VIN, make, model) and/or pick a <b>category</b> to add spec filters (horsepower, axle ratio, GVWR…). Returns every matching vehicle.</p>

        <TruckFilterRail engine={engine} />

        {res && (
          <div className="space-y-2" data-testid="truck-search-results">
            <div data-testid="truck-search-echo" className="text-xs text-slate-600 flex items-center gap-1.5">
              {engine.loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
              Showing <b className="text-slate-900">{res.total}</b> vehicle{res.total === 1 ? "" : "s"}
              {res.synthetic?.in_results ? (
                <b data-testid="tf-synthetic-in-results" className="text-[#B01E28] font-mono">
                  ({res.synthetic.in_results} synthetic)
                </b>
              ) : null}
              {q ? <> for <span className="text-[#E0781A] font-semibold">“{q}”</span></> : null}
              {cat ? <> in <span className="font-semibold">{catName(cat)}</span></> : null}
              {nFilters ? <> · <span className="font-semibold">{nFilters}</span> spec filter{nFilters === 1 ? "" : "s"}</> : null}
              {!q && !cat && !nFilters ? <> (all vehicles)</> : null}
            </div>
            <CaptureGapNote gap={res.capture_gap} testid="tf-capture-gap" />
            {res.items.map((d) => <TruckCard key={d.id} d={d} onCreatePart={openCreate} onOpen={engine.reportOpened} />)}
          </div>
        )}
      </div>

      {cpt && <CreatePartFromTruck open={!!cpt} onOpenChange={(o) => !o && setCpt(null)} donorId={cpt.donorId} donorStock={cpt.donorStock} serialValue={undefined} onCreated={() => setCpt(null)} />}
    </div>
  );
}
